import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AutoComplete, Tooltip, Button, Space, Input, Modal, Popconfirm, Row, Col, Form } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import fetchApi from '../../../utilsTsx/components/system/fetchApi';
import { defaultRtn, RtnType } from '../../../utilsTs/declareTypes';
import antdMessage from '../system/antdMessage';

/** 选项基础结构 */
export interface BaseSelectOption {
    /** 主键 */
    id: string;
    /** 显示名称 */
    name: string;
    /** 详情描述 */
    detail?: string;
}

/** 可编辑选择器属性 */
export interface EditableSelectProps {
    /** 表单值 */
    value?: string;
    /** 表单值变化回调 */
    onChange?: (value: string) => void;
    /** 外部受控选项，传了则不内部请求 */
    options?: BaseSelectOption[];
    /** 外部自定义获取数据 */
    fetchData?: () => Promise<RtnType<any>>;
    /** 外部自定义新增 */
    onAdd?: (name: string, detail?: string, id?: string) => Promise<RtnType<any>>;
    /** 外部自定义删除 */
    onDelete?: (id: string) => Promise<RtnType<any>>;
    /** 必传：默认服务名 */
    defaultService: string;
    /** 内部默认 API 配置 */
    apiConfig: {
        /** apiId */
        apiId: string;
        /** 列表扩展 */
        apiExtend?: string;
        /** 新增扩展 */
        addApiExtend?: string;
        /** 删除扩展 */
        deleteApiExtend?: string;
    };
    /** 占位文本 */
    placeholder?: string;
    /** 表单存值字段，默认 id */
    valueField?: 'id' | 'name';
    /** 允许自由输入（未用） */
    allowFreeInput?: boolean;
    /** 显示新增按钮，默认 true */
    allowAdd?: boolean;
    /** 允许删除选项，默认 true */
    allowDelete?: boolean;
    /** 失焦时转换输入值 */
    blurTransform?: (value: string) => string;
}

const EditableSelect: React.FC<EditableSelectProps> = ({
    value,
    onChange,
    options: externalOptions,
    fetchData: externalFetchData,
    onAdd: externalOnAdd,
    onDelete: externalOnDelete,
    defaultService,
    apiConfig,
    placeholder = '请选择或输入',
    valueField = 'id',
    allowAdd = true,
    allowDelete = true,
    blurTransform,
}) => {
    /** 新增弹窗表单实例 */
    const [form] = Form.useForm();
    /** 内部维护的选项列表 */
    const [innerOptions, setInnerOptions] = useState<BaseSelectOption[]>([]);
    /** 新增按钮 loading */
    const [addLoading, setAddLoading] = useState(false);
    /** 输入框展示值 */
    const [inputValue, setInputValue] = useState<string>('');
    /** 新增弹窗开关 */
    const [isModalOpen, setIsModalOpen] = useState(false);
    /** 搜索关键词，用于前端过滤 */
    const [searchText, setSearchText] = useState('');

    /** 标记 onChange 是否来自下拉选中 */
    const isSelectingRef = useRef(false);

    /** 是否受控（外部传了 options） */
    const isControlled = externalOptions !== undefined;
    /** 当前使用的选项列表 */
    const currentOptions = isControlled ? externalOptions : innerOptions;

    /** 默认获取数据 */
    const defaultFetch = useCallback(async (): Promise<RtnType<any>> => {
        let rtn = defaultRtn();
        if (!apiConfig?.apiId) return rtn;
        rtn = await fetchApi.GET(defaultService, {
            query: {
                apiId: apiConfig.apiId,
                apiExtend: apiConfig.apiExtend || 'showList'
            }
        });
        return rtn;
    }, [apiConfig, defaultService]);

    /** 默认新增 */
    const defaultAdd = async (name: string, detail?: string, id?: string): Promise<RtnType<any>> => {
        let rtn = defaultRtn();
        if (!apiConfig?.apiId) return rtn;
        rtn = await fetchApi.POST(defaultService, {
            body: {
                apiId: apiConfig.apiId,
                apiExtend: apiConfig.addApiExtend || undefined,
                apiData: { id, name, detail }
            }
        });
        return rtn;
    };

    /** 默认删除 */
    const defaultDelete = async (id: string): Promise<RtnType<any>> => {
        let rtn = defaultRtn();
        if (!apiConfig?.apiId) return rtn;
        rtn = await fetchApi.POST(defaultService, {
            body: {
                apiId: apiConfig.apiId,
                apiVariable: id,
                apiExtend: apiConfig.deleteApiExtend || 'remove'
            }
        });
        return rtn;
    };

    /** 加载全量数据 */
    const loadData = useCallback(async () => {
        if (isControlled) return;
        try {
            const fetchFn = externalFetchData || defaultFetch;
            const res = await fetchFn();
            if (res.success) {
                setInnerOptions(res.data || []);
            }
        } catch (error) {
            console.error('获取选项失败:', error);
        }
    }, [isControlled, externalFetchData, defaultFetch]);

    /** 初始加载 */
    useEffect(() => {
        loadData();
    }, [loadData]);

    /** 外部 value 同步到输入框 */
    useEffect(() => {
        if (value !== undefined && value !== '') {
            const matched = currentOptions.find(o => String(o[valueField]) === String(value));
            setInputValue(matched ? matched.name : String(value));
        } else {
            setInputValue('');
        }
    }, [value, currentOptions, valueField]);

    /** 提交新增表单 */
    const handleAddSubmit = async () => {
        try {
            const values = await form.validateFields();
            const trimmedName = values.name?.trim();
            if (!trimmedName) return;

            const addFn = externalOnAdd || defaultAdd;
            if (!addFn) return;

            setAddLoading(true);
            const result = await addFn(trimmedName, values.detail, values.id);
            if (result && result.success) {
                form.resetFields();
                setIsModalOpen(false);
                await loadData();
                if (result.data) {
                    const newValue = valueField === 'id' ? result.data.id : result.data.name;
                    setInputValue(result.data.name || newValue);
                    onChange?.(String(newValue));
                }
                antdMessage('success', '添加成功');
            } else {
                antdMessage('error', '添加失败', { description: result?.message });
            }
        } catch (error: any) {
            if (error?.errorFields) return;
            antdMessage('error', '添加失败', { description: error?.message });
        } finally {
            setAddLoading(false);
        }
    };

    /** 删除选项 */
    const handleDelete = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        const deleteFn = externalOnDelete || defaultDelete;
        if (!deleteFn) return;

        try {
            const rtn = await deleteFn(id);
            if (rtn.success) {
                await loadData();
                antdMessage('success', '删除成功');
                const deletedItem = currentOptions.find((o) => o.id === id);
                if (deletedItem) {
                    const deletedValue = String(deletedItem[valueField]);
                    if (String(value) === deletedValue) {
                        setInputValue('');
                        onChange?.(undefined as any);
                    }
                }
            } else {
                antdMessage('error', '删除失败', { description: rtn?.message });
            }
        } catch (error: any) {
            antdMessage('error', '删除失败', { description: error?.message });
        }
    };

    /** 失焦转换输入值 */
    const handleBlur = () => {
        if (!blurTransform) return;
        const transformed = blurTransform(inputValue ?? '');
        if (transformed !== inputValue) {
            setInputValue(transformed);
            const matched = currentOptions.find(
                o => String(o[valueField]) === transformed || o.name === transformed
            );
            onChange?.(matched ? String(matched[valueField]) : transformed);
        }
    };

    /** 是否可删除 */
    const canDelete = allowDelete && !!(externalOnDelete || apiConfig?.apiId);
    /** 是否可新增 */
    const canAdd = allowAdd && !!(externalOnAdd || apiConfig?.apiId);

    /** 生成并过滤选项 */
    const selectOptions = useMemo(() => {
        const all = currentOptions.map((item) => ({
            value: String(item[valueField]),
            _name: item.name,
            label: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <Tooltip title={item.detail} placement="right">
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.name}
                        </span>
                    </Tooltip>
                    {canDelete && (
                        <Popconfirm
                            title="确定要删除该选项吗？"
                            onConfirm={(e) => handleDelete(e as any, item.id)}
                            onCancel={(e) => e?.stopPropagation()}
                        >
                            <Tooltip title="删除该选项">
                                <DeleteOutlined
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                        color: '#ff4d4f',
                                        cursor: 'pointer',
                                        padding: '4px 6px',
                                        borderRadius: 4,
                                        background: 'rgba(255, 77, 79, 0.1)',
                                        transition: 'background 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        (e.currentTarget as HTMLElement).style.background = 'rgba(255, 77, 79, 0.25)';
                                    }}
                                    onMouseLeave={(e) => {
                                        (e.currentTarget as HTMLElement).style.background = 'rgba(255, 77, 79, 0.1)';
                                    }}
                                />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </div>
            ),
        }));

        if (!searchText) return all;

        const inp = searchText.toLowerCase();
        return all.filter((opt: any) => {
            const name = String(opt._name ?? '').toLowerCase();
            const val = String(opt.value ?? '').toLowerCase();
            return name.includes(inp) || val.includes(inp);
        });
    }, [currentOptions, valueField, canDelete, searchText]);

    return (
        <>
            <Space.Compact style={{ width: '100%' }}>
                <AutoComplete
                    value={inputValue}
                    options={selectOptions}
                    placeholder={placeholder}
                    allowClear
                    style={{ flex: 1 }}
                    onChange={(val) => {
                        setInputValue(val);

                        /** 用户输入时更新搜索词，下拉选中时跳过 */
                        if (!isSelectingRef.current) {
                            setSearchText(val);
                        }
                        isSelectingRef.current = false;

                        const matched = currentOptions.find(
                            o => String(o[valueField]) === val || o.name === val
                        );
                        if (matched) {
                            onChange?.(String(matched[valueField]));
                        } else {
                            onChange?.(val);
                        }
                    }}
                    /** 标记本次变化来自下拉选中 */
                    onSelect={() => {
                        isSelectingRef.current = true;
                    }}
                    /** 关闭下拉时清空搜索词 */
                    onOpenChange={(open) => {
                        if (!open) setSearchText('');
                    }}
                    onBlur={handleBlur}
                />
                {canAdd && (
                    <Tooltip title="新增选项">
                        <Button
                            icon={<PlusOutlined />}
                            onClick={() => setIsModalOpen(true)}
                        />
                    </Tooltip>
                )}
            </Space.Compact>

            <Modal
                title="新增选项"
                open={isModalOpen}
                onOk={handleAddSubmit}
                onCancel={() => {
                    form.resetFields();
                    setIsModalOpen(false);
                }}
                confirmLoading={addLoading}
                destroyOnHidden
                width={500}
            >
                <Form
                    form={form}
                    layout="horizontal"
                    labelCol={{ span: 5 }}
                    wrapperCol={{ span: 19 }}
                    style={{ marginTop: 16 }}
                    /** 阻止 Enter 冒泡到外层表单 */
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <Row gutter={[8, 0]}>
                        <Col span={24}>
                            <Form.Item label="id" name="id" tooltip="唯一标识，可手动/自动生成">
                                <Input
                                    placeholder="请输入 id"
                                    onPressEnter={handleAddSubmit}
                                    onKeyDown={(e) => e.stopPropagation()}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item
                                label="名称"
                                name="name"
                                tooltip="新增选项的展示名称，用于下拉列表选择，建议填写便于识别的名称。"
                                rules={[{ required: true, message: '名称!' }]}
                            >
                                <Input
                                    placeholder="请输入名称（必填）"
                                    onPressEnter={handleAddSubmit}
                                    onKeyDown={(e) => e.stopPropagation()}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item
                                label="详情"
                                name="detail"
                                tooltip="详情信息，鼠标悬浮在下拉选项时会通过 Tooltip 展示。非必填。"
                            >
                                <Input.TextArea
                                    placeholder="请输入详情（选填）"
                                    rows={3}
                                    onKeyDown={(e) => e.stopPropagation()}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </>
    );
};

export default EditableSelect;